import { RefObject, useCallback, useState } from 'react';

import constants from 'constants/constants';
import moment from 'moment-timezone';
import Pptxgen from 'pptxgenjs';

import useUserHook from 'hooks/useUserHook';

import { blobToURL } from 'components/Dashboards/Project/Components/DataStories/dataStories.utils';
import { Widget } from 'components/Dashboards/Reporting/reporting.types';
import {
    getWidgetPositionsFromDOM,
    groupWidgetsByRows,
} from 'components/pages/ReportPage/hooks/useExportReportToPdf/useExportReportToPdf.utils';
import { PptxExportUtils } from 'components/pages/ReportPage/hooks/useExportReportToPptx/useExportReportToPptx.utils';

interface ExportReportToGridPptxParams {
    reportName: string;
    gridContainerRef: RefObject<HTMLDivElement>;
    reportWidgets: Widget[];
    onProgress?: (progress: number, stage: string, capturedWidgets?: number, totalWidgets?: number) => void;
}

export const useExportReportToPptx = () => {
    const [isLoading, setIsLoading] = useState(false);
    const { logo } = useUserHook();

    const exportReportToPptx = useCallback(
        async ({
            reportName,
            gridContainerRef,
            reportWidgets,
            onProgress,
        }: ExportReportToGridPptxParams): Promise<void> => {
            if (isLoading || !gridContainerRef.current) {
                return;
            }

            setIsLoading(true);

            // Allow UI to update with loading state
            await new Promise((resolve) => setTimeout(resolve, 0));

            try {
                onProgress?.(5, 'Initializing export...');

                const container = gridContainerRef.current;

                // Get widget positions from DOM (includes widgetId extracted from data attribute)
                const widgets = getWidgetPositionsFromDOM(container);

                // Create component lookup map and widget-to-component mapping
                const widgetByIdMap = PptxExportUtils.createWidgetByIdMap(reportWidgets);
                const elementToRealWidgetMap = PptxExportUtils.createElementToRealWidgetMap(widgets, widgetByIdMap);

                // Group widgets by rows (using same logic as PDF)
                const rows = groupWidgetsByRows(widgets);

                onProgress?.(10, 'Loading logos...');

                // Get user's company logo
                const logoDataUrl = await PptxExportUtils.getLogoDataUrl(logo);

                // Initialize PowerPoint and define master slide
                const pptx = new Pptxgen();
                PptxExportUtils.defineMasterSlide(pptx, logoDataUrl);

                onProgress?.(15, 'Creating cover page...');

                // Create cover page
                PptxExportUtils.createCoverPage(pptx, reportName, reportWidgets, logoDataUrl);

                onProgress?.(20, 'Capturing widgets...', 0, widgets.length);

                // Capture widgets in batches to prevent browser freezing
                const allWidgets = rows.flat();
                const imageMap = await PptxExportUtils.captureWidgetsInBatches(
                    allWidgets,
                    5,
                    100,
                    (captured, total) => {
                        const captureProgress = 20 + (captured / total) * 40; // 20% to 60%
                        onProgress?.(captureProgress, 'Capturing widgets...', captured, total);
                    },
                );

                onProgress?.(60, 'Creating slides...');

                // Run over all rows in report
                for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
                    // Yield every 3 slides to keep UI responsive
                    if (rowIndex > 0 && rowIndex % 3 === 0) {
                        await new Promise((resolve) => setTimeout(resolve, 0));
                        const slideProgress = 60 + (rowIndex / rows.length) * 30; // 60% to 90%
                        onProgress?.(slideProgress, `Creating slides... (${rowIndex}/${rows.length})`);
                    }

                    const row = rows[rowIndex];
                    const isSingleWidget = row.length === 1;

                    const slide = pptx.addSlide({ masterName: 'MASTER_SLIDE' });

                    // Add slide title - use the first widget's title in the row
                    const firstWidget = row[0];
                    const firstWidgetComponent = elementToRealWidgetMap.get(firstWidget.element);
                    const slideTitle = firstWidgetComponent?.name || 'Component'; // Fallback
                    slide.addText(slideTitle, {
                        x: '2%',
                        y: '3%',
                        fill: { color: '#000000' },
                        fontSize: 16,
                        valign: 'top',
                    });

                    if (isSingleWidget) {
                        // CASE - [single widget in a row] - Use simple percentage-based layout

                        const widget = row[0];
                        const imageData = imageMap.get(widget.element);

                        if (imageData) {
                            const blob = PptxExportUtils.dataURLToBlob(imageData);
                            const blobUrl = blobToURL(blob);
                            const scale = window.devicePixelRatio;

                            // Add single image of the widget
                            slide.addImage({
                                path: blobUrl,
                                y: '13.5%',
                                x: '3%',
                                altText: 'report image',
                                w: widget.width / 196 / scale,
                                h: widget.height / 196 / scale,
                                sizing: { type: 'contain', w: '94%', h: '75%' },
                            });
                        }
                    } else {
                        // CASE - [multiple widgets in a row] - Use grid calculation logic
                        const { scale, scaledRowWidth, scaledRowHeight, scaledGap } =
                            PptxExportUtils.calculateRowScaling(row);

                        // CENTER the row horizontally within the content area
                        const horizontalCenterOffset = (PptxExportUtils.PPTX_CONTENT_WIDTH - scaledRowWidth) / 2;
                        const startX = PptxExportUtils.PPTX_MARGIN + horizontalCenterOffset;

                        // Find the minimum Y position in the row (for alignment)
                        const rowTopY = Math.min(...row.map((w) => w.y));

                        // Center the entire row vertically in available content area
                        const verticalCenterOffset = (PptxExportUtils.PPTX_CONTENT_HEIGHT - scaledRowHeight) / 2;
                        const rowStartY = PptxExportUtils.PPTX_START_Y + verticalCenterOffset;

                        // Place widgets horizontally in the row
                        let currentX = startX;

                        // Run over widgets in row
                        for (let i = 0; i < row.length; i++) {
                            const widget = row[i];
                            const imageData = imageMap.get(widget.element);

                            if (imageData) {
                                // Convert widget dimensions to inches and apply scale
                                const widgetWidthInches = (widget.width / 96) * scale;
                                const widgetHeightInches = (widget.height / 96) * scale;

                                // Calculate Y offset for widgets with different heights in the same row
                                // This maintains relative positioning within the row (scaled)
                                const yOffsetInches = ((widget.y - rowTopY) / 96) * scale;

                                // Final Y position: row start + widget's offset within row (no manual offset needed)
                                const finalY = rowStartY + yOffsetInches;

                                // Convert data URL to blob URL for pptxgenjs
                                const blob = PptxExportUtils.dataURLToBlob(imageData);
                                const blobUrl = blobToURL(blob);

                                // Add single image of the widget
                                slide.addImage({
                                    path: blobUrl,
                                    x: currentX,
                                    y: finalY,
                                    w: widgetWidthInches,
                                    h: widgetHeightInches,
                                    altText: 'report image',
                                });

                                // Move to next widget position (use SCALED gap, like PDF)
                                currentX += widgetWidthInches + scaledGap;
                            }
                        }
                    }

                    // Add footer link
                    PptxExportUtils.addFooterLink(slide, window.location.href);
                }

                onProgress?.(90, 'Finalizing presentation...');

                const dateTimeSuffix = `${moment(new Date()).format(constants.formats.date.yearMonthDay)}_${moment(new Date()).format('HHmm')}`;
                await pptx.writeFile({ fileName: `${reportName}_${dateTimeSuffix}.pptx` });

                onProgress?.(100, 'Export complete!');
            } catch (error) {
                /* empty */
            } finally {
                setIsLoading(false);
            }
        },
        [isLoading, logo],
    );

    return {
        exportReportToPptx,
        isLoadingExportReportToPptx: isLoading,
    };
};
